import { useQueryClient } from '@tanstack/react-query';
import type { ChannelLocationStatusDto } from '@up2stay/up2promo-api-client';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  isScopeLinked,
  isSyncCompleteForScope,
  type ReputationBannerState,
  resolveReputationBannerState,
  shouldPollChannelLocations,
} from '@/core/components/reputation/reputation-sync-utils';
import { useChannelLocations } from '@/core/hooks/use-reputation';
import { useWorkspaceRealtime } from '@/core/hooks/use-workspace-realtime';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import type { RealtimeEvent } from '@/core/types/realtime-events';
import {
  reputationQueryKeys,
  reviewsQueryKeys,
} from '@/core/query-keys/reputation-query-keys';

const POLL_INTERVAL_MS = 30_000;

type ReputationSyncContextValue = {
  scopeLocationId: string | undefined;
  setScopeLocationId: (locationId: string | undefined) => void;
  channelLocations: ChannelLocationStatusDto[];
  isLoading: boolean;
  isScopeLinked: boolean;
  banner: ReputationBannerState;
  isSyncCompleteForScope: boolean;
};

const ReputationSyncContext = createContext<ReputationSyncContextValue | null>(
  null,
);

export function ReputationSyncProvider({ children }: { children: ReactNode }) {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const queryClient = useQueryClient();
  const [scopeLocationId, setScopeLocationId] = useState<string | undefined>();

  const { data: channelLocations = [], isLoading } = useChannelLocations(
    workspaceId,
    {},
    {
      refetchInterval: (query) => {
        const rows = query.state.data ?? [];
        if (!shouldPollChannelLocations(rows)) return false;
        const bannerState = resolveReputationBannerState(rows, scopeLocationId);
        if (bannerState.variant === 'reanalyzing') {
          return 10_000;
        }
        return POLL_INTERVAL_MS;
      },
    },
  );

  const invalidateSyncQueries = useCallback(() => {
    if (!workspaceId) return;
    void queryClient.invalidateQueries({
      queryKey: reputationQueryKeys.channelLocations(workspaceId),
    });
    void queryClient.invalidateQueries({
      queryKey: reputationQueryKeys.root,
    });
    void queryClient.invalidateQueries({
      queryKey: reviewsQueryKeys.all(workspaceId),
    });
  }, [queryClient, workspaceId]);

  const onRealtimeEvent = useCallback(
    (event: RealtimeEvent) => {
      if (
        event.type === 'review_sync.started' ||
        event.type === 'review_sync.completed' ||
        event.type === 'review_sync.failed' ||
        event.type === 'review.created' ||
        event.type === 'review.updated'
      ) {
        invalidateSyncQueries();
      }
    },
    [invalidateSyncQueries],
  );

  useWorkspaceRealtime({
    workspaceId,
    enabled: !!workspaceId,
    onEvent: onRealtimeEvent,
  });

  const banner = useMemo(
    () => resolveReputationBannerState(channelLocations, scopeLocationId),
    [channelLocations, scopeLocationId],
  );

  const syncComplete = useMemo(
    () => isSyncCompleteForScope(channelLocations, scopeLocationId),
    [channelLocations, scopeLocationId],
  );

  const scopeLinked = useMemo(
    () => isScopeLinked(channelLocations, scopeLocationId),
    [channelLocations, scopeLocationId],
  );

  const value = useMemo(
    () => ({
      scopeLocationId,
      setScopeLocationId,
      channelLocations,
      isLoading,
      isScopeLinked: scopeLinked,
      banner,
      isSyncCompleteForScope: syncComplete,
    }),
    [
      scopeLocationId,
      channelLocations,
      isLoading,
      scopeLinked,
      banner,
      syncComplete,
    ],
  );

  return (
    <ReputationSyncContext.Provider value={value}>
      {children}
    </ReputationSyncContext.Provider>
  );
}

/** Pages call this when their location filter changes (context-aware banner). */
export function useReputationScopeLocation(locationId: string | undefined) {
  const ctx = useContext(ReputationSyncContext);
  useEffect(() => {
    ctx?.setScopeLocationId(locationId);
    return () => {
      ctx?.setScopeLocationId(undefined);
    };
  }, [ctx, locationId]);
}

export function useReputationSync() {
  const ctx = useContext(ReputationSyncContext);
  if (!ctx) {
    throw new Error(
      'useReputationSync must be used within ReputationSyncProvider',
    );
  }
  return ctx;
}

export function useReputationSyncOptional() {
  return useContext(ReputationSyncContext);
}
